import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { BookOpen, Award, CheckCircle, Sparkles, TrendingUp, Shield, Zap } from 'lucide-react';
export function PeptideMasteryDetailSection() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.05
  });
  return <section className="relative py-12 sm:py-16 md:py-20 px-5 sm:px-6 bg-gradient-to-b from-black via-purple-950/20 to-black overflow-hidden" data-id="element-2118" data-mp="igeu8kf9ud1m">
      {/* Mystical background effects - matching existing sections */}
      <div className="absolute inset-0 opacity-10" style={{
      backgroundImage: `radial-gradient(circle at 20% 50%, rgba(168, 85, 247, 0.3) 0%, transparent 50%),
                         radial-gradient(circle at 80% 50%, rgba(59, 130, 246, 0.3) 0%, transparent 50%)`
    }} data-id="element-2119" data-mp="3lyga9guw6u2v" />

      {/* Glowing orbs */}
      <div className="absolute top-1/4 left-1/3 w-64 h-64 sm:w-96 sm:h-96 bg-purple-600/10 rounded-full blur-2xl sm:blur-3xl animate-pulse-slow" data-id="element-2120" data-mp="1g5rlz40n8x72" />
      <div className="absolute bottom-1/4 right-1/3 w-64 h-64 sm:w-96 sm:h-96 bg-blue-600/10 rounded-full blur-2xl sm:blur-3xl animate-pulse-slow" style={{
      animationDelay: '1s'
    }} data-id="element-2121" data-mp="3gp0g0hhouheo" />

      <div ref={ref} className="relative z-10 max-w-5xl mx-auto" data-id="element-2122" data-mp="3qlvckuuei7qb">
        {/* Main Headline */}
        <h2 className="text-3xl sm:text-4xl md:text-6xl font-serif text-center mb-8 sm:mb-12 leading-tight text-stone-100" style={{
        fontFamily: 'Cinzel, serif',
        textShadow: '0 0 30px rgba(168, 85, 247, 0.4)'
      }} data-psi-pro-details-section data-id="element-2123" data-mp="3uoiandho0ovy">
          Here's Exactly What You're Getting When You Join{' '}
          <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-purple-500 bg-clip-text text-transparent" style={{
          textShadow: '0 0 40px rgba(168, 85, 247, 0.6)'
        }} data-id="element-2124" data-mp="36xme6329l5id">
            PSI PRO
          </span>
          :
        </h2>

        <p className="text-lg sm:text-xl md:text-2xl text-stone-300 text-center mb-10 sm:mb-16" data-id="element-2125" data-mp="2j8lcqzrumuml">
          Let me break down the{' '}
          <span className="text-amber-400 font-bold" data-id="element-2126" data-mp="3mifxwt85yy3h">COMPLETE</span> package...
        </p>

        {/* PEPTIDE MASTERY 2.0 - MAIN SECTION */}
        <div className="relative mb-12 sm:mb-20" data-id="element-2127" data-mp="3hitiqe8rz7s1">
          <div className="absolute inset-0 bg-gradient-radial from-amber-600/20 via-transparent to-transparent blur-3xl" data-id="element-2128" data-mp="34njklojj67" />

          <div className="relative bg-gradient-to-br from-amber-950/60 via-stone-950/80 to-black border-2 sm:border-4 border-amber-600/60 p-6 sm:p-10 md:p-12 rounded-lg shadow-[0_0_16px_rgba(217,119,6,0.26)] sm:shadow-[0_0_60px_rgba(217,119,6,0.5)]" data-id="element-2129" data-mp="3rb91vevt62ei">
            {/* Corner accents */}
            <div className="absolute top-0 left-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-2130" data-mp="3cmcnz172lv5z">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500 to-transparent" data-id="element-2131" data-mp="38jpxp4q1siaw" />
              <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-amber-500 to-transparent" data-id="element-2132" data-mp="ru10el7qpnth" />
            </div>
            <div className="absolute top-0 right-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-2133" data-mp="nvtjnqda0mia">
              <div className="absolute top-0 right-0 w-full h-1 bg-gradient-to-l from-amber-500 to-transparent" data-id="element-2134" data-mp="vbopi81du5yb" />
              <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-amber-500 to-transparent" data-id="element-2135" data-mp="10dnutngoncxw" />
            </div>
            <div className="absolute bottom-0 left-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-2136" data-mp="qe6mszdykmyr">
              <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500 to-transparent" data-id="element-2137" data-mp="qg9gbtebky88" />
              <div className="absolute bottom-0 left-0 w-1 h-full bg-gradient-to-t from-amber-500 to-transparent" data-id="element-2138" data-mp="1j2uebcpaw8kr" />
            </div>
            <div className="absolute bottom-0 right-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-2139" data-mp="14cwn0nw2r2rc">
              <div className="absolute bottom-0 right-0 w-full h-1 bg-gradient-to-l from-amber-500 to-transparent" data-id="element-2140" data-mp="19cizgv42wez3" />
              <div className="absolute bottom-0 right-0 w-1 h-full bg-gradient-to-t from-amber-500 to-transparent" data-id="element-2141" data-mp="1x1kdqyzlekca" />
            </div>

            {/* Icon */}
            <div className="flex justify-center mb-6 sm:mb-8" data-id="element-2142" data-mp="e50j0dzqfd9a">
              <div className="relative" data-id="element-2143" data-mp="e6ldk3m1qw75">
                <div className="absolute inset-0 bg-amber-500/40 blur-xl animate-pulse-slow" data-id="element-2144" data-mp="95xgb0785j4h" />
                <CheckCircle className="relative w-12 h-12 sm:w-16 sm:h-16 text-amber-400 drop-shadow-[0_0_15px_rgba(251,191,36,0.8)]" data-id="element-2145" data-mp="46uju45b8l84" />
              </div>
            </div>

            {/* Title */}
            <div className="text-center mb-6 sm:mb-8" data-id="element-2146" data-mp="wvi7259mdkob">
              <h3 className="text-xl sm:text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-amber-400 mb-4" style={{
              fontFamily: 'Cinzel, serif',
              textShadow: '0 0 40px rgba(217, 119, 6, 0.6)'
            }} data-id="element-2147" data-mp="wuyqoszmb4ce">
                PEPTIDE MASTERY 2.0 — THE COMPLETE BIOLOGICAL OPTIMIZATION
                BLUEPRINT
              </h3>

              <div className="inline-block px-4 sm:px-6 py-2 sm:py-3 bg-green-600/30 border-2 border-green-500/50 rounded-lg mt-4" data-id="element-2148" data-mp="1fmj2wkfg103n">
                <p className="text-green-400 font-bold text-lg sm:text-xl" data-id="element-2149" data-mp="1atq227n3ibwu">
                  Included as part of every PSI PRO membership.
                </p>
              </div>

              <p className="text-stone-400 text-base sm:text-lg mt-4" data-id="element-2150" data-mp="1n6k0h3f7vu8y">
                (Modules unlock one at a time as part of your monthly
                membership; a full-course unlock option will be offered later)
              </p>
            </div>

            {/* Description */}
            <div className="space-y-4 sm:space-y-6 text-base sm:text-lg text-stone-300 mb-8 sm:mb-12" data-id="element-2151" data-mp="223kujdkvs1al">
              <p className="text-lg sm:text-xl text-stone-100 font-semibold text-center" data-id="element-2152" data-mp="263l6mrkic0eh">
                This is the most comprehensive, scientifically validated,
                in-depth peptide training ever created.
              </p>

              <div className="bg-amber-950/30 border-l-4 border-amber-500 p-4 sm:p-6 space-y-3" data-id="element-2153" data-mp="2fteg9n13a5g3">
                <p className="text-stone-100" data-id="element-2154" data-mp="2kv3utcnit18x">
                  The same knowledge base used when{' '}
                  <span className="font-bold text-amber-400" data-id="element-2155" data-mp="2pq9gcad0gdwh">
                    $120,000/year private clients
                  </span>{' '}
                  come to Brenden with "impossible" cases.
                </p>
                <p className="text-stone-100" data-id="element-2156" data-mp="29u1bmihyxngw">
                  The same protocols implemented when{' '}
                  <span className="font-bold text-amber-400" data-id="element-2157" data-mp="253kzd1pvw7w4">
                    Olympic athletes
                  </span>{' '}
                  need an edge.
                </p>
                <p className="text-stone-100" data-id="element-2158" data-mp="2iwizf1d9uo60">
                  The same frameworks applied when{' '}
                  <span className="font-bold text-amber-400" data-id="element-2159" data-mp="2dxz9yqzgyu8w">
                    doctors send him their toughest cases
                  </span>
                  .
                </p>
              </div>

              <p className="text-xl sm:text-2xl font-bold text-center text-amber-400 mt-6 sm:mt-8" data-id="element-2160" data-mp="yeuvfhp06l29">
                Here's what's inside:
              </p>
            </div>

            {/* 130+ PEPTIDES SECTION */}
            <div className="mb-10 sm:mb-16" data-id="element-2161" data-mp="353d8eejg23y8">
              <div className="bg-gradient-to-br from-purple-950/40 to-black border-2 border-purple-500/50 p-6 sm:p-8 rounded-lg" data-id="element-2162" data-mp="353n2oebiux9q">
                <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6" data-id="element-2163" data-mp="3a4143dimb8ys">
                  <Sparkles className="w-7 h-7 sm:w-10 sm:h-10 text-purple-400 flex-shrink-0 mt-1" data-id="element-2164" data-mp="2vd1y8a962yiw" />
                  <h4 className="text-xl sm:text-3xl font-bold text-purple-400" data-id="element-2165" data-mp="30abt45fbs2e4">
                    130+ PEPTIDES DECODED AT THE MOLECULAR LEVEL
                  </h4>
                </div>

                <div className="space-y-4 text-base sm:text-lg text-stone-300" data-id="element-2166" data-mp="2qfjvx2pnnhjv">
                  <p className="text-lg sm:text-xl text-stone-100 font-semibold" data-id="element-2167" data-mp="2cfr7je1lpdf2">
                    Complete modules on over 130 powerful peptides — including
                    rare ones most "experts" have never heard of (and never will
                    unless they go to the source like Brenden has).
                  </p>

                  <p className="text-amber-400 font-bold" data-id="element-2168" data-mp="fk35p2av34k2">
                    This is the ONLY course in the world that truly covers all
                    of Khavinson's peptides.
                  </p>

                  <p className="text-stone-100 font-semibold" data-id="element-2169" data-mp="2ulhoqqj0wad1">
                    Not summaries. Not overviews.
                  </p>

                  <p className="text-lg sm:text-xl text-stone-100 font-bold mb-4" data-id="element-2170" data-mp="2ku3t4tu6zup6">
                    Deep mechanistic breakdowns of:
                  </p>

                  <div className="space-y-3 pl-4 sm:pl-6 border-l-2 border-purple-500/30 text-sm sm:text-base" data-id="element-2171" data-mp="2frux1om0wciu">
                    <p className="flex items-start gap-3" data-id="element-2172" data-mp="26tjbi100k4x9">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" data-id="element-2173" data-mp="26u2pj6mjm960" />
                      <span data-id="element-2174" data-mp="21w0xhw752zie">
                        <span className="font-bold text-purple-400" data-id="element-2175" data-mp="21wstv98k919q">
                          Exact mechanisms of action
                        </span>{' '}
                        (what it actually does in your body at the cellular and
                        molecular level)
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-2176" data-mp="cl5n99u4hagv">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" data-id="element-2177" data-mp="cjm7dkcdp1fm" />
                      <span data-id="element-2178" data-mp="hd765elspzfv">
                        <span className="font-bold text-purple-400" data-id="element-2179" data-mp="mbiflo86i7xk">
                          Optimal dosing protocols
                        </span>{' '}
                        (the doses that actually work, based on real research
                        and real results)
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-2180" data-mp="3ur25qedjza8e">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" data-id="element-2181" data-mp="3utodof4cvrr5" />
                      <span data-id="element-2182" data-mp="2lev3et1b7jy">
                        <span className="font-bold text-purple-400" data-id="element-2183" data-mp="1a1m932rnkrau">
                          Synergistic combinations
                        </span>{' '}
                        (how to stack peptides for exponential rather than
                        additive effects)
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-2184" data-mp="4atgwqhhqfss">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" data-id="element-2185" data-mp="92l6axowufhc" />
                      <span data-id="element-2186" data-mp="94xqlhi551xk">
                        <span className="font-bold text-purple-400" data-id="element-2187" data-mp="e1xublripsli">
                          Timing and absorption optimization
                        </span>{' '}
                        (when to dose for maximum bioavailability)
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-2188" data-mp="3mhr99w67aw0g">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" data-id="element-2189" data-mp="3mg6h2d7czp5r" />
                      <span data-id="element-2190" data-mp="3ri5hpfbps7z0">
                        <span className="font-bold text-purple-400" data-id="element-2191" data-mp="11r8zdffwi95g">
                          Safety considerations
                        </span>{' '}
                        (real risks based on actual science, not internet
                        fear-mongering)
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-2192" data-mp="2m9je8vud5d1c">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" data-id="element-2193" data-mp="2mbe48okhrvca" />
                      <span data-id="element-2194" data-mp="2hgqe6l9uasvl">
                        <span className="font-bold text-purple-400" data-id="element-2195" data-mp="2cjhrc7dqy0o0">
                          Product type identification
                        </span>{' '}
                        (how to tell if you're buying Type 1, 2, or 3
                        bioregulators)
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-2196" data-mp="1yj72f9vzyw5h">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-1" data-id="element-2197" data-mp="1ylt8t675j5m4" />
                      <span data-id="element-2198" data-mp="1tmgp2ubkuenr">
                        <span className="font-bold text-purple-400" data-id="element-2199" data-mp="3sxq6hgw6t7pm">
                          Where to source high-quality versions
                        </span>{' '}
                        (because knowing what to take is worthless if what
                        you're taking is bunk)
                      </span>
                    </p>
                  </div>

                  <div className="bg-purple-950/30 p-4 sm:p-6 rounded-lg mt-6" data-id="element-2200" data-mp="1r1imt41zecd8">
                    <p className="text-lg sm:text-xl text-stone-100 font-bold" data-id="element-2201" data-mp="1ccmjtles7pjh">
                      This goes far beyond what you'll find in any other course,
                      book, or training program.
                    </p>
                    <p className="text-purple-400 font-semibold mt-2 text-sm sm:text-base" data-id="element-2202" data-mp="24y4hy7agpn3y">
                      Because no other program was created by someone who's
                      actually gone to the source.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* 54 PROTOCOLS SECTION */}
            <div className="mb-10 sm:mb-16" data-id="element-2203" data-mp="jlukwg6c0msg">
              <div className="bg-gradient-to-br from-cyan-950/40 to-black border-2 border-cyan-500/50 p-6 sm:p-8 rounded-lg" data-id="element-2204" data-mp="olpg2vx53yjb">
                <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6" data-id="element-2205" data-mp="32soxwy1x8lu0">
                  <Zap className="w-7 h-7 sm:w-10 sm:h-10 text-cyan-400 flex-shrink-0 mt-1" data-id="element-2206" data-mp="2xunf8e8eqoj2" />
                  <h4 className="text-xl sm:text-3xl font-bold text-cyan-400" data-id="element-2207" data-mp="3btwkwjyzm08s">
                    54 BRENDEN HENRY PROTOCOLS
                  </h4>
                </div>

                <p className="text-xl sm:text-2xl text-cyan-300 font-bold mb-6" data-id="element-2208" data-mp="37qgpfqynm5xo">
                  Precision-Engineered Synergistic Protocols That Create
                  "Impossible" Transformations
                </p>

                <div className="space-y-4 text-base sm:text-lg text-stone-300" data-id="element-2209" data-mp="2svak767dub5e">
                  <p className="text-lg sm:text-xl text-stone-100 font-semibold" data-id="element-2210" data-mp="2sxexger68mic">
                    This is where theory becomes transformation.
                  </p>

                  <p data-id="element-2211" data-mp="20donsy2brh4i">
                    These aren't generic templates you could find with enough
                    Google searching.
                  </p>
                  <p data-id="element-2212" data-mp="292wzoj8nbzee">These aren't "here's what might work" suggestions.</p>

                  <p className="text-lg sm:text-xl text-cyan-400 font-bold" data-id="element-2213" data-mp="290spxko2g667">
                    These are precision-engineered protocols Brenden has
                    developed through:
                  </p>

                  <div className="space-y-3 pl-4 sm:pl-6 border-l-2 border-cyan-500/30 text-sm sm:text-base" data-id="element-2214" data-mp="2nrjjoy97xfby">
                    <p className="flex items-start gap-3" data-id="element-2215" data-mp="2sr5ul11kqjhl">
                      <span className="text-cyan-400 flex-shrink-0" data-id="element-2216" data-mp="2sspc0uyyv0ky">→</span>
                      <span data-id="element-2217" data-mp="2wqwvvyoq8i0d">
                        Years of translating original Russian research
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-2218" data-mp="36q5jm9t010ea">
                      <span className="text-cyan-400 flex-shrink-0" data-id="element-2219" data-mp="36pmbtlx2bsdz">→</span>
                      <span data-id="element-2220" data-mp="17el82tgeg8od">
                        Extensive experimentation with 1,500+ participants
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-2221" data-mp="14svbrwsuugpo">
                      <span className="text-cyan-400 flex-shrink-0" data-id="element-2222" data-mp="14uevjxdpz9w7">→</span>
                      <span data-id="element-2223" data-mp="1013tsanrljb5">
                        Real-world application with private $15,000−$120,000
                        transformation clients
                      </span>
                    </p>
                    <p className="flex items-start gap-3" data-id="element-2224" data-mp="q5a923zw6jia">
                      <span className="text-cyan-400 flex-shrink-0" data-id="element-2225" data-mp="1nrg05h3d37zm">→</span>
                      <span data-id="element-2226" data-mp="1npmzqjjcdk02">
                        Deep mechanistic understanding of peptide synergies
                        nobody else knows
                      </span>
                    </p>
                  </div>

                  <div className="bg-cyan-950/30 p-4 sm:p-6 rounded-lg mt-6" data-id="element-2227" data-mp="18up8jtzxj0kk">
                    <p className="text-lg sm:text-xl text-stone-100 font-bold mb-4" data-id="element-2228" data-mp="18wta45acvnho">
                      Each protocol shows you:
                    </p>
                    <div className="space-y-2 pl-4 text-sm sm:text-base" data-id="element-2229" data-mp="3tjwi6kvzsxt7">
                      <p className="flex items-start gap-3" data-id="element-2230" data-mp="2lkpg8j4hxj3u">
                        <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" data-id="element-2231" data-mp="2lj4mgvq0a471" />
                        <span data-id="element-2232" data-mp="2pkq2ur1r10lz">Exactly what peptides to use</span>
                      </p>
                      <p className="flex items-start gap-3" data-id="element-2233" data-mp="2bmt53rbk6o5q">
                        <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" data-id="element-2234" data-mp="2glcrfsu2e1ym" />
                        <span data-id="element-2235" data-mp="21wq5h89xmbyf">Precise dosing schedules</span>
                      </p>
                      <p className="flex items-start gap-3" data-id="element-2236" data-mp="26nq0g7zhqjya">
                        <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" data-id="element-2237" data-mp="26p9io44pj52p" />
                        <span data-id="element-2238" data-mp="1syefn8gw3i71">How to stack them for maximum synergy</span>
                      </p>
                      <p className="flex items-start gap-3" data-id="element-2239" data-mp="1xvweu753uj1u">
                        <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" data-id="element-2240" data-mp="1iy419kj4c4qa" />
                        <span data-id="element-2241" data-mp="1ny07zrj7aa9j">Expected timeline for results</span>
                      </p>
                      <p className="flex items-start gap-3" data-id="element-2242" data-mp="14b5azsvzzgd5">
                        <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-1" data-id="element-2243" data-mp="14dri5rez7tuu" />
                        <span data-id="element-2244" data-mp="10bwh8o0yjihl">How to source everything you need</span>
                      </p>
                    </div>
                  </div>

                  {/* Protocol Categories */}
                  <div className="mt-8" data-id="element-2245" data-mp="1996w0jczhpr9">
                    <p className="text-xl sm:text-2xl font-bold text-cyan-400 mb-6 text-center" data-id="element-2246" data-mp="1naavyu0q65xq">
                      Here are all 54 protocols you're getting:
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6" data-id="element-2247" data-mp="1j74q3nhiygrs">
                      {/* REGENERATION & HEALING */}
                      <div className="bg-emerald-950/30 border border-emerald-500/30 p-4 sm:p-6 rounded-lg" data-id="element-2248" data-mp="1s0zn288ht0wc">
                        <h5 className="text-lg sm:text-xl font-bold text-emerald-400 mb-4" data-id="element-2249" data-mp="26zl85y19i8r3">
                          REGENERATION & HEALING:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-2250" data-mp="mnmhjifhr5gm">
                          <li data-id="element-2251" data-mp="rm7hrhuqtp52">• Liver Regeneration</li>
                          <li data-id="element-2252" data-mp="rkmmtr4io46o">• Kidney Repair</li>
                          <li data-id="element-2253" data-mp="wlk66719j49r">• Bone Healing</li>
                          <li data-id="element-2254" data-mp="wltulq6nt3dc">• Accelerated Healing</li>
                          <li data-id="element-2255" data-mp="11kx2die0xnp0">• Regenerate Spinal Discs</li>
                        </ul>
                      </div>

                      {/* NEUROLOGICAL & COGNITIVE */}
                      <div className="bg-purple-950/30 border border-purple-500/30 p-4 sm:p-6 rounded-lg" data-id="element-2256" data-mp="3usb5cvxolo0">
                        <h5 className="text-lg sm:text-xl font-bold text-purple-400 mb-4" data-id="element-2257" data-mp="dusyrb0u5ny0">
                          NEUROLOGICAL & COGNITIVE:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-2258" data-mp="imu9gsicv6lx">
                          <li data-id="element-2259" data-mp="3hb02fhj93l6s">• Traumatic Brain Injury Recovery</li>
                          <li data-id="element-2260" data-mp="3m9t66i8qjxtr">• Stroke Recovery</li>
                          <li data-id="element-2261" data-mp="3m8i0udzn6i0j">• Alzheimer's Disease Regeneration</li>
                          <li data-id="element-2262" data-mp="23e6w42ufo5z8">• Parkinson's Disease Regeneration</li>
                          <li data-id="element-2263" data-mp="1zdd879gzqr6c">• Cognitive Decline / Brain Fog Solution</li>
                          <li data-id="element-2264" data-mp="1uetj5073oz9r">
                            • Improve Speed of Learning and Memory Retention
                          </li>
                          <li data-id="element-2265" data-mp="1penmt6pvyolc">• ADHD Calm Focus Protocol</li>
                        </ul>
                      </div>

                      {/* MENTAL HEALTH & NEUROCHEMISTRY */}
                      <div className="bg-blue-950/30 border border-blue-500/30 p-4 sm:p-6 rounded-lg" data-id="element-2266" data-mp="1fq5nyv162rkc">
                        <h5 className="text-lg sm:text-xl font-bold text-blue-400 mb-4" data-id="element-2267" data-mp="1bnso2n93pnvh">
                          MENTAL HEALTH & NEUROCHEMISTRY:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-2268" data-mp="11q4qlpk4229m">
                          <li data-id="element-2269" data-mp="11s8voatqx7bh">• Anti-Anxiety</li>
                          <li data-id="element-2270" data-mp="wqrvyimzzqwh">• Mood Enhancement and Depression Relief</li>
                          <li data-id="element-2271" data-mp="vig9f0c1or9s">• Anhedonia Fix & Pleasure Re-Sensitization</li>
                          <li data-id="element-2272" data-mp="vgcakwt06igd">• Addiction Support Stack</li>
                        </ul>
                      </div>

                      {/* METABOLIC & ENDOCRINE */}
                      <div className="bg-amber-950/30 border border-amber-500/30 p-4 sm:p-6 rounded-lg" data-id="element-2273" data-mp="lixs6rnirjlf">
                        <h5 className="text-lg sm:text-xl font-bold text-amber-400 mb-4" data-id="element-2274" data-mp="hie042hsz665">
                          METABOLIC & ENDOCRINE:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-2275" data-mp="7sug2tict69p">
                          <li data-id="element-2276" data-mp="2thsg6jnrv62">• Diabetes Control</li>
                          <li data-id="element-2277" data-mp="2uasyps0xofr">• Fix Hypothyroidism</li>
                          <li data-id="element-2278" data-mp="3u13q9ijp53f0">• Male Hormone Optimization</li>
                          <li data-id="element-2279" data-mp="3tyhlfqo6x20h">• Female Hormone Optimization</li>
                          <li data-id="element-2280" data-mp="3py7dw9kr23ji">• Low Energy and Chronic Fatigue Fix</li>
                          <li data-id="element-2281" data-mp="3kzdzmexn28ia">
                            • Stubborn Fat Loss and Muscle Gain Enhancement
                          </li>
                        </ul>
                      </div>

                      {/* CARDIOVASCULAR & CIRCULATORY */}
                      <div className="bg-red-950/30 border border-red-500/30 p-4 sm:p-6 rounded-lg" data-id="element-2282" data-mp="25ispxxjpwo2k">
                        <h5 className="text-lg sm:text-xl font-bold text-red-400 mb-4" data-id="element-2283" data-mp="29ggs9lnqpj4h">
                          CARDIOVASCULAR & CIRCULATORY:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-2284" data-mp="1hsor03hh5chw">
                          <li data-id="element-2285" data-mp="1ltidqtj6kl97">• Ultimate Heart Protocol</li>
                          <li data-id="element-2286" data-mp="1qs202v1orz23">• Blood Vessel and Artery Support</li>
                          <li data-id="element-2287" data-mp="1qpp9jthv4gdf">• Anemia Fix</li>
                        </ul>
                      </div>

                      {/* IMMUNE & AUTOIMMUNE */}
                      <div className="bg-green-950/30 border border-green-500/30 p-4 sm:p-6 rounded-lg" data-id="element-2288" data-mp="y3wey76q081q">
                        <h5 className="text-lg sm:text-xl font-bold text-green-400 mb-4" data-id="element-2289" data-mp="131637jididnz">
                          IMMUNE & AUTOIMMUNE:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-2290" data-mp="21ea92rupjpki">
                          <li data-id="element-2291" data-mp="21c7io6pn7uf9">• Immune System Fortification</li>
                          <li data-id="element-2292" data-mp="2f4x8ync7fli6">• Autoimmune Support</li>
                          <li data-id="element-2293" data-mp="2b43h5ivn5mk0">• Cancer Recovery Support</li>
                        </ul>
                      </div>

                      {/* ANTI-AGING & LONGEVITY */}
                      <div className="bg-pink-950/30 border border-pink-500/30 p-4 sm:p-6 rounded-lg" data-id="element-2294" data-mp="1ngdd9xo2vn6w">
                        <h5 className="text-lg sm:text-xl font-bold text-pink-400 mb-4" data-id="element-2295" data-mp="1ihivmz1uk4od">
                          ANTI-AGING & LONGEVITY:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-2296" data-mp="1reb1ax64438p">
                          <li data-id="element-2297" data-mp="37st2jj6h8xaq">• Biological Age Reversal</li>
                          <li data-id="element-2298" data-mp="32v9p2lhlsoju">• Elderly Lifespan Stack</li>
                          <li data-id="element-2299" data-mp="32urjuzcyqzzs">• Skin Rejuvenation</li>
                          <li data-id="element-2300" data-mp="3bbif69ybnue6">• Hair Growth and Health</li>
                        </ul>
                      </div>

                      {/* RESPIRATORY & ENVIRONMENTAL */}
                      <div className="bg-teal-950/30 border border-teal-500/30 p-4 sm:p-6 rounded-lg" data-id="element-2301" data-mp="31du7ykjj4uf4">
                        <h5 className="text-lg sm:text-xl font-bold text-teal-400 mb-4" data-id="element-2302" data-mp="36cxfqcqw9eqs">
                          RESPIRATORY & ENVIRONMENTAL:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-2303" data-mp="3u3b5txpx53iq">
                          <li data-id="element-2304" data-mp="2vp1e2vdkx0t">• Lung Power</li>
                          <li data-id="element-2305" data-mp="3kczrdh8lzbxl">• Ultimate Cold Tolerance</li>
                          <li data-id="element-2306" data-mp="3kcgb04ylwvlo">• Ultimate Anti-Hypoxiant</li>
                          <li data-id="element-2307" data-mp="3pb1ag261bb92">• "Hold Your Breath for Longer" Protocol</li>
                          <li data-id="element-2308" data-mp="gw9jewtguot9">• Radiation Resilience and Recovery Regimen</li>
                        </ul>
                      </div>

                      {/* PERFORMANCE & ATHLETICS */}
                      <div className="bg-orange-950/30 border border-orange-500/30 p-4 sm:p-6 rounded-lg" data-id="element-2309" data-mp="2t6azlvpj3t1q">
                        <h5 className="text-lg sm:text-xl font-bold text-orange-400 mb-4" data-id="element-2310" data-mp="2p4xszjem3ltr">
                          PERFORMANCE & ATHLETICS:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-2311" data-mp="2k7p9nfhq9bqx">
                          <li data-id="element-2312" data-mp="2f82z5dtaa9lt">• Athletic Performance Enhancement</li>
                          <li data-id="element-2313" data-mp="3cujuidigid4x">• Military Personnel Protocol</li>
                        </ul>
                      </div>

                      {/* PAIN & INFLAMMATION */}
                      <div className="bg-rose-950/30 border border-rose-500/30 p-4 sm:p-6 rounded-lg" data-id="element-2314" data-mp="334796j2tifxq">
                        <h5 className="text-lg sm:text-xl font-bold text-rose-400 mb-4" data-id="element-2315" data-mp="2y5njq8p0mm0m">
                          PAIN & INFLAMMATION:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-2316" data-mp="3vmup7k2yj4ry">
                          <li data-id="element-2317" data-mp="3qsgmsxpvnxds">• Chronic Pain Management</li>
                          <li data-id="element-2318" data-mp="20bruzr7botp">• Neuropathy Support</li>
                          <li data-id="element-2319" data-mp="21wl8m1s51pz">• Chronic Prostatitis Cure</li>
                        </ul>
                      </div>

                      {/* SENSORY & RESTORATION */}
                      <div className="bg-indigo-950/30 border border-indigo-500/30 p-4 sm:p-6 rounded-lg" data-id="element-2320" data-mp="bx6mv8o9n738">
                        <h5 className="text-lg sm:text-xl font-bold text-indigo-400 mb-4" data-id="element-2321" data-mp="gynjut3mq9ej">
                          SENSORY & RESTORATION:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-2322" data-mp="pwz6yl5aep28">
                          <li data-id="element-2323" data-mp="unxr5vtec7ar">• Eye Health and Vision Enhancement</li>
                          <li data-id="element-2324" data-mp="uqtpvmd2j009">• Reverse Hearing Loss</li>
                          <li data-id="element-2325" data-mp="zm90m5fs4fsg">• Restore Olfaction</li>
                        </ul>
                      </div>

                      {/* DIGESTIVE & ORAL */}
                      <div className="bg-lime-950/30 border border-lime-500/30 p-4 sm:p-6 rounded-lg" data-id="element-2326" data-mp="18o8lx9xtu617">
                        <h5 className="text-lg sm:text-xl font-bold text-lime-400 mb-4" data-id="element-2327" data-mp="35omtez033cyv">
                          DIGESTIVE & ORAL:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-2328" data-mp="30rmmklveu66p">
                          <li data-id="element-2329" data-mp="3eirfl549w082">• Digestive Health</li>
                          <li data-id="element-2330" data-mp="39qq61qyhonlq">• Reverse Gum Disease and Periodontitis</li>
                        </ul>
                      </div>

                      {/* SLEEP & RECOVERY */}
                      <div className="bg-violet-950/30 border border-violet-500/30 p-4 sm:p-6 rounded-lg" data-id="element-2331" data-mp="3oh79ffuopnl9">
                        <h5 className="text-lg sm:text-xl font-bold text-violet-400 mb-4" data-id="element-2332" data-mp="1aob3fsdzyrd">
                          SLEEP & RECOVERY:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-2333" data-mp="3tez1qaz8rbpz">
                          <li data-id="element-2334" data-mp="232t12lqbslzg">• Sleep Optimization</li>
                        </ul>
                      </div>

                      {/* QUALITY OF LIFE */}
                      <div className="bg-fuchsia-950/30 border border-fuchsia-500/30 p-4 sm:p-6 rounded-lg" data-id="element-2335" data-mp="1owreyputhouf">
                        <h5 className="text-lg sm:text-xl font-bold text-fuchsia-400 mb-4" data-id="element-2336" data-mp="1symb7fxw5c1c">
                          QUALITY OF LIFE:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-2337" data-mp="1f4vej859dmn1">
                          <li data-id="element-2338" data-mp="1k3ybe5b34mjx">• Supercharged Libido Stack</li>
                          <li data-id="element-2339" data-mp="157gy38jc4xuq">• Enhanced Tanning Protocol</li>
                          <li data-id="element-2340" data-mp="1a7lfd0s2hwm0">• Pregnancy Symptom Relief</li>
                        </ul>
                      </div>

                      {/* RARE CONDITIONS */}
                      <div className="bg-sky-950/30 border border-sky-500/30 p-4 sm:p-6 rounded-lg" data-id="element-2341" data-mp="w9er8ccx7azj">
                        <h5 className="text-lg sm:text-xl font-bold text-sky-400 mb-4" data-id="element-2342" data-mp="1176t9z7a4dhg">
                          RARE CONDITIONS:
                        </h5>
                        <ul className="space-y-2 text-xs sm:text-sm text-stone-300" data-id="element-2343" data-mp="2vl7rchv08wgh">
                          <li data-id="element-2344" data-mp="7zppjrr43nws">• Huntington's Disease Recovery</li>
                          <li data-id="element-2345" data-mp="823jtydyww3a">
                            • Duchenne Muscular Dystrophy or Spinal-Bulbar
                            Muscular Atrophy Healing
                          </li>
                          <li data-id="element-2346" data-mp="3udr3sgqb04q2">• Sarcopenia Recovery</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="bg-cyan-950/30 p-4 sm:p-6 rounded-lg mt-8" data-id="element-2347" data-mp="3lf5kukgx6nor">
                    <p className="text-lg sm:text-xl text-stone-100 font-bold mb-2" data-id="element-2348" data-mp="3gg2f0xt48dfq">
                      Each one is a complete roadmap.
                    </p>
                    <p className="text-lg sm:text-xl text-stone-100 font-bold mb-2" data-id="element-2349" data-mp="3bishsvzhixhc">
                      Each one is based on years of research and real human
                      results.
                    </p>
                    <p className="text-lg sm:text-xl text-stone-100 font-bold" data-id="element-2350" data-mp="19n08tktq968s">
                      Each one gives you exactly what you need to implement it
                      successfully.
                    </p>
                    <p className="text-xl sm:text-2xl text-cyan-400 font-bold mt-6" data-id="element-2351" data-mp="2sk7ezu9zsp3a">
                      Having ONE protocol that actually works for YOUR specific
                      situation is worth more than the entire investment.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* 1,193 SCIENTIFIC CITATIONS */}
            <div className="mb-10 sm:mb-16" data-id="element-2352" data-mp="29lboou198uuq">
              <div className="bg-gradient-to-br from-blue-950/40 to-black border-2 border-blue-500/50 p-6 sm:p-8 rounded-lg" data-id="element-2353" data-mp="29mefl1ykuxan">
                <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6" data-id="element-2354" data-mp="2dpb4faepnpn1">
                  <BookOpen className="w-7 h-7 sm:w-10 sm:h-10 text-blue-400 flex-shrink-0 mt-1" data-id="element-2355" data-mp="2inup77hkivdt" />
                  <h4 className="text-xl sm:text-3xl font-bold text-blue-400" data-id="element-2356" data-mp="3p1txm98e4yls">
                    BACKED WITH 1,193 SCIENTIFIC CITATIONS
                  </h4>
                </div>

                <div className="space-y-4 text-base sm:text-lg text-stone-300" data-id="element-2357" data-mp="oz6jv48n0nkf">
                  <p className="text-lg sm:text-xl text-stone-100 font-semibold" data-id="element-2358" data-mp="tx81dkqf0ets">
                    Every claim is backed by peer-reviewed research.
                  </p>

                  <p data-id="element-2359" data-mp="k75apr6rncym">All available in the Master Citation List.</p>

                  <p className="text-blue-400 font-bold" data-id="element-2360" data-mp="k5arqif21ux6">
                    We sell this citation list separately for $1,193 (one dollar
                    per citation).
                  </p>

                  <p className="text-lg sm:text-xl text-stone-100 font-bold" data-id="element-2361" data-mp="aaag3jdm5wpm">
                    But it's included inside Peptide Mastery for free.
                  </p>

                  <div className="bg-blue-950/30 p-4 sm:p-6 rounded-lg mt-6" data-id="element-2362" data-mp="19c70mobiz06">
                    <p className="text-stone-100 mb-2" data-id="element-2363" data-mp="1qmpjiz6l3tcd">
                      This isn't bro-science.
                    </p>
                    <p className="text-stone-100 mb-2" data-id="element-2364" data-mp="1vkzeb8oecrxg">
                      This isn't anecdotal evidence.
                    </p>
                    <p className="text-stone-100 mb-4" data-id="element-2365" data-mp="3g28g6f2a8z8z">
                      This isn't "I read on Reddit that..."
                    </p>
                    <p className="text-lg sm:text-xl text-blue-400 font-bold" data-id="element-2366" data-mp="3punxrb9o3ltc">
                      This is decades of scientific validation, centralized and
                      simplified into the most valuable biological enhancement
                      resource in the world.
                    </p>
                  </div>

                  <div className="space-y-3 pl-4 sm:pl-6 border-l-2 border-blue-500/30 mt-6 text-sm sm:text-base" data-id="element-2367" data-mp="3tv7y0npjf5jp">
                    <p data-id="element-2368" data-mp="cmun5kly6bnm">
                      When Brenden makes a claim, it's backed by original
                      research.
                    </p>
                    <p data-id="element-2369" data-mp="2ee7ibhy4556h">
                      When he recommends a protocol, it's validated by studies
                      most people don't even know exist.
                    </p>
                    <p data-id="element-2370" data-mp="2z4bhi2a7n0ch">
                      When he states a mechanism, it's supported by
                      peer-reviewed science.
                    </p>
                  </div>

                  <p className="text-xl sm:text-2xl text-center text-stone-100 font-bold mt-6" data-id="element-2371" data-mp="384q372r36hgi">
                    You're not getting opinions.
                  </p>
                  <p className="text-xl sm:text-2xl text-center text-blue-400 font-bold" data-id="element-2372" data-mp="3d54aujorzpe0">
                    You're getting facts.
                  </p>
                </div>
              </div>
            </div>

            {/* MASS SPECTROMETRY VERIFICATION */}
            <div className="mb-10 sm:mb-16" data-id="element-2373" data-mp="3quy0v5c0ofpb">
              <div className="bg-gradient-to-br from-emerald-950/40 to-black border-2 border-emerald-500/50 p-6 sm:p-8 rounded-lg" data-id="element-2374" data-mp="3qxare6vubydz">
                <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6" data-id="element-2375" data-mp="4o9ja0dfhrf8">
                  <Shield className="w-7 h-7 sm:w-10 sm:h-10 text-emerald-400 flex-shrink-0 mt-1" data-id="element-2376" data-mp="4m6luv9y4w0h" />
                  <h4 className="text-xl sm:text-3xl font-bold text-emerald-400" data-id="element-2377" data-mp="9kq9r183ohvh">
                    MASS SPECTROMETRY VERIFICATION
                  </h4>
                </div>

                <div className="space-y-4 text-base sm:text-lg text-stone-300" data-id="element-2378" data-mp="1p4qvsdob8x8x">
                  <p className="text-lg sm:text-xl text-stone-100 font-semibold" data-id="element-2379" data-mp="1k7h7508qwpm5">
                    We don't just tell you what to take.
                  </p>

                  <p className="text-lg sm:text-xl text-stone-100 font-semibold" data-id="element-2380" data-mp="2cw5zoavqjmma">
                    We show you PROOF that what you're buying is actually what
                    the label claims.
                  </p>

                  <div className="bg-red-950/30 border-l-4 border-red-500 p-4 sm:p-6 my-6" data-id="element-2381" data-mp="234rvhpsu9ymt">
                    <p className="text-lg sm:text-xl text-red-400 font-bold mb-4" data-id="element-2382" data-mp="1y5oz0tqxd0qg">
                      Because here's a dirty secret of the peptide industry:
                    </p>
                    <div className="space-y-2 text-stone-300 text-sm sm:text-base" data-id="element-2383" data-mp="2quvv7m2wpe8j">
                      <p data-id="element-2384" data-mp="2bdnuh1v0enz0">Not all peptides are created equal.</p>
                      <p data-id="element-2385" data-mp="2bc30pegir927">Some vendors are selling underdosed products.</p>
                      <p data-id="element-2386" data-mp="2gd8ulflx12db">Some are selling completely bunk products.</p>
                      <p data-id="element-2387" data-mp="21fq7ehovyx7z">Some are selling contaminated products.</p>
                    </div>
                  </div>

                  <p className="text-stone-100 font-semibold" data-id="element-2388" data-mp="26fckmr4psdgs">
                    And you'd never know unless you had access to mass
                    spectrometry testing.
                  </p>

                  <p className="text-lg sm:text-xl text-emerald-400 font-bold" data-id="element-2389" data-mp="2z1xb7pdd8lja">
                    Which you now do.
                  </p>

                  <div className="bg-emerald-950/30 p-4 sm:p-6 rounded-lg mt-6" data-id="element-2390" data-mp="2kbzvlxti545p">
                    <p className="text-stone-100 mb-2 text-sm sm:text-base" data-id="element-2391" data-mp="2pbkuk88o9cif">
                      <span className="font-bold text-emerald-400" data-id="element-2392" data-mp="3d1ovu8ye1069">
                        Included in the course:
                      </span>{' '}
                      Mass spec analysis of popular peptide products, so you can
                      see exactly what's real and what's fake.
                    </p>
                    <p className="text-lg sm:text-xl text-emerald-400 font-bold mt-4" data-id="element-2393" data-mp="11ev5ireg0onb">
                      This alone has saved our clients thousands of dollars in
                      wasted purchases.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* LIFETIME UPDATES */}
            <div className="mb-10 sm:mb-16" data-id="element-2394" data-mp="igs9vtre3tgj">
              <div className="bg-gradient-to-br from-purple-950/40 to-black border-2 border-purple-500/50 p-6 sm:p-8 rounded-lg" data-id="element-2395" data-mp="ihtvgs617yci">
                <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6" data-id="element-2396" data-mp="wdyr3jwgtk3n">
                  <TrendingUp className="w-7 h-7 sm:w-10 sm:h-10 text-purple-400 flex-shrink-0 mt-1" data-id="element-2397" data-mp="sdetkf7zcegz" />
                  <h4 className="text-xl sm:text-3xl font-bold text-purple-400" data-id="element-2398" data-mp="27tngehx6yv7m">
                    LIFETIME UPDATES
                  </h4>
                </div>

                <div className="space-y-4 text-base sm:text-lg text-stone-300" data-id="element-2399" data-mp="10hwdpfeq8we8">
                  <p className="text-stone-100" data-id="element-2400" data-mp="15hj2mm3x412v">
                    The course has already been updated multiple times since its
                    original release.
                  </p>

                  <p className="text-lg sm:text-xl text-purple-400 font-bold" data-id="element-2401" data-mp="1eigvq9ctio6o">
                    It's now 10x bigger than when it launched.
                  </p>

                  <p className="text-lg sm:text-xl text-stone-100 font-bold" data-id="element-2402" data-mp="hqdbvbwfvods">
                    And you get access to EVERY future update, forever.
                  </p>

                  <div className="space-y-3 pl-4 sm:pl-6 border-l-2 border-purple-500/30 mt-6 text-sm sm:text-base" data-id="element-2403" data-mp="qscvmbyu96kf">
                    <p data-id="element-2404" data-mp="vl5yeuaqy4tv">As new research emerges...</p>
                    <p data-id="element-2405" data-mp="3v7w7bhh45xo7">
                      As Brenden refines protocols based on new client
                      results...
                    </p>
                    <p data-id="element-2406" data-mp="31h6jmlropgv">
                      As the science advances and new peptides become
                      available...
                    </p>
                  </div>

                  <p className="text-xl sm:text-2xl text-center text-purple-400 font-bold mt-6" data-id="element-2407" data-mp="3mrncjfpga3nj">
                    You stay at the forefront.
                  </p>
                </div>
              </div>
            </div>

            {/* PROGRESSIVE MODULE JOURNEY */}
            <div className="mb-8 sm:mb-12" data-id="element-2408" data-mp="38quiew329aky">
              <div className="bg-gradient-to-br from-amber-950/40 to-black border-2 border-amber-500/50 p-6 sm:p-8 rounded-lg" data-id="element-2409" data-mp="38rvv14zq654s">
                <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6" data-id="element-2410" data-mp="2z0ri0atm0ucl">
                  <Award className="w-7 h-7 sm:w-10 sm:h-10 text-amber-400 flex-shrink-0 mt-1" data-id="element-2411" data-mp="2yyotxwc0pbai" />
                  <h4 className="text-xl sm:text-3xl font-bold text-amber-400" data-id="element-2412" data-mp="2ux399ot8rspr">
                    A PROGRESSIVE MODULE-BY-MODULE JOURNEY
                  </h4>
                </div>

                <div className="space-y-4 text-base sm:text-lg text-stone-300" data-id="element-2413" data-mp="2l1s3hvzt9vu5">
                  <p className="text-lg sm:text-xl text-stone-100 font-semibold" data-id="element-2414" data-mp="ctlmnw24cuwt">
                    Peptide Mastery 2.0 is strategically sequenced to take you
                    from foundational understanding to complete mastery without
                    overwhelming you.
                  </p>

                  <p className="text-lg sm:text-xl text-amber-400 font-bold mb-4" data-id="element-2415" data-mp="8pdt9lkpuqs9">
                    Each unlock introduces a new phase:
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2416" data-mp="3uxn9oscnjk60">
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-2417" data-mp="3uzrm5yom9rhw">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-2418" data-mp="3h4fi30okmoob">Module 1:</p>
                      <p className="text-sm" data-id="element-2419" data-mp="3l8deh3mihldo">
                        Introduction & Basics + Mass Spectrometry Analysis
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-2420" data-mp="3c6dv44o0y57k">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-2421" data-mp="2sku8wpsopjbs">Module 2:</p>
                      <p className="text-sm" data-id="element-2422" data-mp="2nmsq85z67m0u">
                        Sleep Optimization, Circadian Rhythm Repair & Stress
                        Adaptation
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-2423" data-mp="2xk717q2brilr">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-2424" data-mp="3bjzp7dmgvkq1">Module 3:</p>
                      <p className="text-sm" data-id="element-2425" data-mp="36leo7bze4x0j">
                        Gut Health, Digestive Repair & Detoxification
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-2426" data-mp="3l9ja0otqponj">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-2427" data-mp="3gbq2nsgpaolo">Module 4:</p>
                      <p className="text-sm" data-id="element-2428" data-mp="3uc0uaxpu477w">
                        Brain Health, Cognitive Enhancement & Neuro-Regeneration
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-2429" data-mp="1t0test2mix5">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-2430" data-mp="3t0devk0zliiv">Module 5:</p>
                      <p className="text-sm" data-id="element-2431" data-mp="bjdfwqkg4m5x">
                        Cardiovascular Regeneration, Metabolic Health & Thyroid
                        Function
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-2432" data-mp="kjs3jwkvudcl">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-2433" data-mp="fl7conrit60t">Module 6:</p>
                      <p className="text-sm" data-id="element-2434" data-mp="fnbduxy1bqxe">
                        Joint, Bone & Cartilage Regeneration
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-2435" data-mp="pkg356n6g2if">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-2436" data-mp="2wam74j3opyv4">Module 7:</p>
                      <p className="text-sm" data-id="element-2437" data-mp="2rb17e6goxmhc">
                        Energy Production, Sports Performance & Endurance
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-2438" data-mp="16uti58zgy9pn">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-2439" data-mp="1btd3p8a5hjhh">Module 8:</p>
                      <p className="text-sm" data-id="element-2440" data-mp="x2dym78ixd2n">
                        Men's Hormonal Health, Vitality & Fertility
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-2441" data-mp="o0wk2tarljg2">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-2442" data-mp="s3t6yw7c81pt">Module 9:</p>
                      <p className="text-sm" data-id="element-2443" data-mp="e41nify1xt4f">
                        Women's Hormonal Balance, Thyroid & Reproductive Health
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-2444" data-mp="j4pmpowfpg9q">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-2445" data-mp="28fq8ozuvmrx9">Module 10:</p>
                      <p className="text-sm" data-id="element-2446" data-mp="2dg4fkekqrvtp">
                        Injury Repair & Vision Restoration
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-2447" data-mp="27045mz9qwkak">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-2448" data-mp="22110zfxogg34">Module 11:</p>
                      <p className="text-sm" data-id="element-2449" data-mp="1x2hgljyqfccv">
                        Total Immune Defense, Respiratory Health & Infection
                        Control
                      </p>
                    </div>
                    <div className="bg-amber-950/30 p-4 rounded border border-amber-500/30" data-id="element-2450" data-mp="2prexpjr7j341">
                      <p className="font-bold text-amber-400 mb-1" data-id="element-2451" data-mp="2l0ezmb6cqezy">Module 12:</p>
                      <p className="text-sm" data-id="element-2452" data-mp="2kyc3d9elvpms">
                        Ultimate Longevity, Skin Rejuvenation & Detoxification
                      </p>
                    </div>
                  </div>

                  <div className="bg-amber-950/30 p-4 sm:p-6 rounded-lg mt-6" data-id="element-2453" data-mp="3dikbzuhi1nc8">
                    <p className="text-lg sm:text-xl text-stone-100 font-bold mb-4" data-id="element-2454" data-mp="38ooh50wj8gla">
                      This systematic approach ensures:
                    </p>
                    <div className="space-y-2 pl-4 text-sm sm:text-base" data-id="element-2455" data-mp="3kzgwsrpqsdf9">
                      <p className="flex items-start gap-3" data-id="element-2456" data-mp="3pzlcwgmqn64y">
                        <span className="text-amber-400 flex-shrink-0" data-id="element-2457" data-mp="3py0icr0fbn73">→</span>
                        <span data-id="element-2458" data-mp="3uz7oymqtygc6">You're not overwhelmed with information</span>
                      </p>
                      <p className="flex items-start gap-3" data-id="element-2459" data-mp="3rbx9c2mmgs9">
                        <span className="text-amber-400 flex-shrink-0" data-id="element-2460" data-mp="7t6zqjwan07m">→</span>
                        <span data-id="element-2461" data-mp="ckfabj9d0rmd">
                          You build comprehensive understanding methodically
                        </span>
                      </p>
                      <p className="flex items-start gap-3" data-id="element-2462" data-mp="hj0dnrjwrrf1">
                        <span className="text-amber-400 flex-shrink-0" data-id="element-2463" data-mp="mj4r18pis611">→</span>
                        <span data-id="element-2464" data-mp="rhpleri3bvhk">
                          You can implement protocols as you learn them
                        </span>
                      </p>
                      <p className="flex items-start gap-3" data-id="element-2465" data-mp="1o7i40qfg0jbq">
                        <span className="text-amber-400 flex-shrink-0" data-id="element-2466" data-mp="1jarteimeqfxp">→</span>
                        <span data-id="element-2467" data-mp="1xymmw188oobu">
                          You develop true mastery rather than surface knowledge
                        </span>
                      </p>
                    </div>
                  </div>

                  <div className="text-center mt-8" data-id="element-2468" data-mp="159fca3vig8a8">
                    <p className="text-lg sm:text-xl text-stone-100 font-bold mb-2" data-id="element-2469" data-mp="15cbbrwn0b50s">
                      As you move through the modules, you won't just collect
                      random knowledge.
                    </p>
                    <p className="text-xl sm:text-2xl text-amber-400 font-bold" data-id="element-2470" data-mp="1a9jv40jw5f3m">
                      You'll build systematic mastery of biological optimization.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Final Summary */}
            <div className="text-center space-y-4 sm:space-y-6 mt-8 sm:mt-12" data-id="element-2471" data-mp="2t89depxsl1gp">
              <p className="text-xl sm:text-2xl md:text-3xl font-bold text-stone-100" data-id="element-2472" data-mp="2tacd61oqy8ym">
                Again, that's{' '}
                <span className="text-amber-400" data-id="element-2473" data-mp="2jabri93engr9">Peptide Mastery 2.0</span>.
              </p>
              <p className="text-2xl sm:text-3xl font-bold text-green-400" data-id="element-2474" data-mp="2o8mzsfe1xj7d">
                Included as a core part of every PSI PRO membership.
              </p>
            </div>
          </div>
        </div>

        {/* Transition to next section */}
        <div className="text-center mt-12 sm:mt-16" data-id="element-2475" data-mp="3gx0x0sxbgbhv">
          <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-stone-100 mb-4" data-id="element-2476" data-mp="3297k5fzm7n2z">
            Now here's where this gets truly{' '}
            <span className="text-purple-400" data-id="element-2477" data-mp="3766enzn6xw1p">unprecedented</span>...
          </p>
        </div>
      </div>
    </section>;
}